import { useEffect, useState } from "react";
import { CommonModelInfo, LinkedAccount } from "../../../../models/Entities";
import { filterLinkedAccountTypeaheadOptions } from "../../../shared/linked-account-typeahead/LinkedAccountTypeaheadFilter";
import LinkedAccountTypeaheadRenderOption from "../../../shared/linked-account-typeahead/LinkedAccountTypeaheadRenderOption";
import CustomerLinkedAccountSection from "../customer-linked-accounts/CustomerLinkedAccountSection";
import LeftPanelTypeahead from "../shared/LeftPanelTypeahead";
import { Button, ButtonVariant } from "@merge-api/merge-javascript-shared";
import { Check, Copy, ExternalLink } from "lucide-react";
import { getDashboardBaseUrl } from "../../../../environmentConfig";

interface Props {
  integrationID: string;
  isFetchBlueprint: boolean;
  selectedTestLinkedAccount: undefined | string;
  setSelectedTestLinkedAccount: (s: string | undefined) => void;
  setReportFileID: (x: string | undefined) => void;
  fetchTestLinkedAccounts: () => void;
  isLoadingTestLinkedAccounts: boolean;
  testLinkedAccounts: LinkedAccount[];
  selectedTestCommonModel: undefined | string;
  setSelectedTestCommonModel: (s: string | undefined) => void;
  testCommonModels: undefined | CommonModelInfo[];
}

export const getLinkedAccountDashboardBaseUrl = (): string | null => getDashboardBaseUrl();

/**
 * Renders Select fields for configuring a Blueprint test run
 */
const BlueprintLeftPanelConsoleSelect = ({
  integrationID,
  isFetchBlueprint,
  selectedTestLinkedAccount,
  setSelectedTestLinkedAccount,
  fetchTestLinkedAccounts,
  setReportFileID,
  isLoadingTestLinkedAccounts,
  testLinkedAccounts,
  selectedTestCommonModel,
  setSelectedTestCommonModel,
  testCommonModels,
}: Props) => {
  const [hasCopied, setHasCopied] = useState(false);
  const dashboardBaseUrl = getLinkedAccountDashboardBaseUrl();

  useEffect(() => {
    setHasCopied(false);
  }, [selectedTestLinkedAccount]);

  useEffect(() => {
    if (!hasCopied) return;
    const timer = setTimeout(() => setHasCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [hasCopied]);

  return (
    <div className="flex flex-col space-y-4">
      <div className="flex flex-col space-y-2">
        <LeftPanelTypeahead
          loading={isLoadingTestLinkedAccounts}
          title="Select Linked Account"
          placeholder="Select Linked Account"
          onChange={(_, option: LinkedAccount | undefined) => {
            setSelectedTestLinkedAccount(option?.id ?? undefined);
            setReportFileID(undefined);
          }}
          value={testLinkedAccounts.find(
            (linkedAccount) => linkedAccount?.id === selectedTestLinkedAccount
          )}
          getOptionLabel={(option: LinkedAccount) => {
            return option.end_user?.organization_name;
          }}
          renderOption={(option: LinkedAccount) => (
            <LinkedAccountTypeaheadRenderOption option={option} />
          )}
          filterOptions={(options: LinkedAccount[], state: any) => {
            return filterLinkedAccountTypeaheadOptions(options, state);
          }}
          options={testLinkedAccounts}
        />
        {selectedTestLinkedAccount && (
          <div className="flex flex-col space-y-1">
            <Button
              size="sm"
              variant={ButtonVariant.TextBlue}
              leftIcon={
                hasCopied ? <Check size={12} className="text-green-50" /> : <Copy size={12} />
              }
              onClick={async () => {
                await navigator.clipboard.writeText(selectedTestLinkedAccount);
                setHasCopied(true);
              }}
            >
              {hasCopied ? "Copied!" : "Copy ID"}
            </Button>
            {dashboardBaseUrl && (
              <Button
                size="sm"
                variant={ButtonVariant.TextBlue}
                leftIcon={<ExternalLink size={12} />}
                onClick={() =>
                  window.open(
                    `${dashboardBaseUrl}/linked-accounts/account/${selectedTestLinkedAccount}/logs/api-requests`,
                    "_blank"
                  )
                }
              >
                View Requests In Dashboard
              </Button>
            )}
          </div>
        )}
        {isFetchBlueprint && (
          <CustomerLinkedAccountSection
            integrationID={integrationID}
            fetchTestLinkedAccounts={fetchTestLinkedAccounts}
          />
        )}
      </div>
      {testCommonModels !== undefined && (
        <LeftPanelTypeahead
          title={`Common Model ${testCommonModels?.[0]?.name}`}
          placeholder={`Select ${testCommonModels?.[0]?.name ?? "Common Model"}`}
          onChange={(_, option) => {
            setSelectedTestCommonModel(option ?? undefined);
          }}
          value={selectedTestCommonModel}
          options={testCommonModels.map(({ id }) => id)}
        />
      )}
    </div>
  );
};

export default BlueprintLeftPanelConsoleSelect;
